import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import Checkbox from '@/components/UI/FormElements/Checkbox/Checkbox';
import SearchFilterBar from '@/components/UI/SearchFilterBar';
import { errorToast, successToast } from '@/helpers';
import { _isNotBlank } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import type { UserApiId } from '@/model/User.model';
import type { UserGroupMembershipUpdatePayload } from '@/model/UserGroups.model';
import { ApiService, ResponseType } from '@/support/ApiService';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { type FC, useMemo, useState } from 'react';
import { fieldValidationMessage } from './UserGroupErrors';
import styles from './UserGroupMemberPicker.module.scss';
import { useUserGroups } from './useUserGroups';

export interface ManageUserGroupsUser {
  api_id: UserApiId;
  name: string;
}

interface ManageUserGroupsModalProps {
  closeModal: () => void;
  user: ManageUserGroupsUser;
  onSaved: () => void;
}

/**
 * Sets which groups a single user belongs to, from the Users table.
 *
 * Deliberately one user at a time: a multi-user version would need tri-state checkboxes for groups
 * only some of the selection belongs to, and the group edit modal already covers bulk membership
 * from the other direction.
 */
const ManageUserGroupsModal: FC<ManageUserGroupsModalProps> = ({ closeModal, user, onSaved }) => {
  const { groups, loading, loaded, failed, refetch } = useUserGroups();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Array<string> | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  const currentGroupApiIds = useMemo(
    () =>
      groups.reduce<Array<string>>((acc, group) => {
        if (group.member_api_ids.includes(user.api_id)) {
          acc.push(group.api_id);
        }
        return acc;
      }, []),
    [groups, user.api_id]
  );

  const selectedApiIds = selected ?? currentGroupApiIds;
  const selectedSet = useMemo(() => new Set(selectedApiIds), [selectedApiIds]);

  const visibleGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!_isNotBlank(query)) {
      return groups;
    }
    return groups.reduce<typeof groups>((acc, group) => {
      if (group.name.toLowerCase().includes(query)) {
        acc.push(group);
      }
      return acc;
    }, []);
  }, [groups, search]);

  const toggleGroup = (apiId: string): void => {
    setSelected(selectedSet.has(apiId) ? selectedApiIds.filter((id) => id !== apiId) : [...selectedApiIds, apiId]);
  };

  const selectAllVisible = (): void => {
    const additions = visibleGroups.reduce<Array<string>>((acc, group) => {
      if (!selectedSet.has(group.api_id)) {
        acc.push(group.api_id);
      }
      return acc;
    }, []);
    setSelected([...selectedApiIds, ...additions]);
  };

  const onSubmit = async () => {
    if (!loaded) {
      return;
    }

    setSubmitting(true);
    const body: UserGroupMembershipUpdatePayload = {
      user_group_api_ids: selectedApiIds as UserGroupMembershipUpdatePayload['user_group_api_ids'],
    };
    const response = await ApiService.call({
      endpoint: 'POST /api/v1/users/{userId}/user-groups',
      path: { userId: user.api_id },
      body,
      options: { onError: { toast: false, capture: false, throw: false } },
    });
    setSubmitting(false);

    if (response.type === ResponseType.Error) {
      const message = fieldValidationMessage(response, 'user_group_api_ids');
      errorToast(_isNotBlank(message) ? message : `Could not update the groups for ${user.name}`);
      ExceptionHandler.captureException(
        new InVivoError('Could not update a user’s groups', {
          cause: response.error,
          slug: 'user-user-groups-update',
        })
      );
      return;
    }

    successToast(`Updated groups for ${user.name}`);
    onSaved();
    closeModal();
  };

  return (
    <div className="center mw6 ui-card" data-test-component="ManageUserGroupsModal" data-test-element="container">
      <div className="pa4" data-test-component="ManageUserGroupsModal" data-test-element="header">
        <h3 className="normal f4" data-testid="title">
          Group details
        </h3>
      </div>
      <div className="ph4 pb3" data-test-component="ManageUserGroupsModal" data-test-element="body">
        <Banner info className="mb3" dismiss={false} testId="membership-warning">
          Removing users from this group may affect their access to items assigned to the group.
        </Banner>
        <div className={styles.panel}>
          <div className="pa2 bb b--moon-gray" data-test-component="ManageUserGroupsModal" data-test-element="search">
            <SearchFilterBar searchPlaceholderText="Search" handleSearchInput={setSearch} searchQuery={search} />
            <div className="flex items-center justify-between pt2" data-test-element="actions">
              <div>
                <button
                  type="button"
                  className="link-button f6"
                  onClick={selectAllVisible}
                  disabled={submitting || loading || failed}
                  data-testid="select-all-button"
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="link-button f6 ml3"
                  onClick={() => setSelected([])}
                  disabled={submitting || loading || failed || selectedApiIds.length === 0}
                  data-testid="clear-selection-button"
                >
                  Clear selection
                </button>
              </div>
              <span className="f6 dark-gray" data-testid="selected-count">
                {selectedApiIds.length} selected
              </span>
            </div>
          </div>
          <div className={styles.list} data-test-component="ManageUserGroupsModal" data-test-element="list">
            {loading && (
              <p className="f6 dark-gray pa3 ma0" data-testid="groups-loading">
                Loading groups…
              </p>
            )}
            {failed && (
              <div className="pa3" data-testid="groups-error">
                <p className="f6 red ma0 pb2">The groups could not be loaded.</p>
                <button type="button" className="link-button f6" onClick={refetch} data-testid="retry-groups-button">
                  Try again
                </button>
              </div>
            )}
            {loaded && groups.length === 0 && (
              <p className="f6 dark-gray pa3 ma0" data-testid="no-groups-message">
                No groups yet
              </p>
            )}
            {loaded && groups.length > 0 && visibleGroups.length === 0 && (
              <p className="f6 dark-gray pa3 ma0" data-testid="no-matches-message">
                No groups match that search
              </p>
            )}
            {visibleGroups.map((group) => (
              <div
                key={group.api_id}
                className={`${styles.row} pa2`}
                data-test-element="group"
                data-test-key={group.api_id}
              >
                <Checkbox
                  name={group.api_id}
                  id={`group-${group.api_id}`}
                  checked={selectedSet.has(group.api_id)}
                  onChange={() => toggleGroup(group.api_id)}
                  disabled={submitting}
                  label={group.name}
                  testPrefix={`group-${group.api_id}`}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div
        className="bt b--moon-gray bg-white pa3"
        data-test-component="ManageUserGroupsModal"
        data-test-element="footer"
      >
        <Button className="mr2" onClick={onSubmit} disabled={submitting || !loaded} testId="submit-button">
          {submitting ? 'Saving…' : 'Save changes'}
        </Button>
        <Button plain onClick={closeModal} disabled={submitting} testId="cancel-button">
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default ManageUserGroupsModal;
